'use client';
import React from 'react';
import { Icon } from '@iconify/react';
import { cn } from '@heroui/react';
import { Link } from 'react-router';
import Typography from '../typography/Typography';
import { getButtonVariantColorsByUserRole } from '../../theme/colors';
import { AnimatePresence, m } from 'framer-motion';
import { useIsMobile } from '../../hooks/useMobile';
export enum SidebarItemType {
  Nest = 'nest',
}
export type SidebarItem = {
  key: string;
  title: string;
  icon?: string;
  href?: string;
  type?: SidebarItemType.Nest;
  startContent?: React.ReactNode;
  endContent?: React.ReactNode;
  items?: SidebarItem[];
  className?: string;
};
export type SidebarProps = {
  items: SidebarItem[];
  iconClassName?: string;
  className?: string;
  selectedKey?: string;
  isPending?: boolean;
  startTransition?: (callback: () => void) => void;
  userRole?: string;
  onItemClick?: () => void;
};
const Sidebar = React.forwardRef<HTMLElement, SidebarProps>(
  (
    {
      items,
      iconClassName,
      className,
      selectedKey,
      isPending,
      startTransition,
      userRole,
      onItemClick,
    },
    ref
  ) => {
    const [internalIsPending, internalStartTransition] = React.useTransition();
    const currentIsPending = isPending || internalIsPending;
    const isMobile = useIsMobile();
    const [openKeys, setOpenKeys] = React.useState<Set<string>>(() => {
      if (!selectedKey) return new Set();
      for (const item of items) {
        if (item.items?.some((subItem) => subItem.key === selectedKey)) {
          return new Set([item.key]);
        }
      }
      return new Set();
    });
    const primaryColors = React.useMemo(
      () => getButtonVariantColorsByUserRole('primary', userRole),
      [userRole]
    );
    const parentKey = React.useMemo(() => {
      if (!selectedKey) return null;
      for (const item of items) {
        if (item.items?.some((subItem) => subItem.key === selectedKey)) {
          return item.key;
        }
      }
      return null;
    }, [items, selectedKey]);

    const toggleSection = React.useCallback((key: string) => {
      setOpenKeys((prev) => {
        if (prev.has(key)) {
          return new Set();
        }
        return new Set([key]);
      });
    }, []);

    React.useEffect(() => {
      if (!isMobile || !parentKey) return;
      setOpenKeys((prev) => {
        if (prev.has(parentKey)) return prev;
        const next = new Set(prev);
        next.add(parentKey);
        return next;
      });
    }, [isMobile, parentKey]);
    const renderItem = React.useCallback(
      (item: SidebarItem, isNested?: boolean, isLast?: boolean) => {
        if (!item.href) return null;
        const isSelected = selectedKey === item.key;
        const showIcon = !(isMobile && isNested);
        return (
          <li
            key={item.key}
            className={cn(
              isMobile &&
                !isNested &&
                ((userRole === 'partner' && item.key === 'dashboard') ||
                  (userRole !== 'partner' && item.key === 'accounts'))
                ? 'mb-8'
                : null,
              isMobile && isNested && isLast ? 'mb-4' : null
            )}
          >
            <Link
              to={item.href}
              onClick={onItemClick}
              className={cn(
                'flex items-center gap-2 px-4 py-2.5 min-h-[2.75rem] rounded-large',
                'text-sm font-semibold',
                isSelected ? null : 'hover:bg-default-300/20 dark:hover:bg-default-200/40',
                'transition-colors duration-150 ease-in-out',
                'w-full',
                isSelected ? `text-white` : 'text-auth-muted',
                isMobile && isNested ? 'pl-[3.125rem] pr-4 py-[.3125rem] min-h-0 font-normal' : null
              )}
              style={isSelected ? { backgroundColor: primaryColors.background } : undefined}
            >
              {showIcon &&
                (item.icon ? (
                  <div className="icon size-6">
                    <Icon
                      className={cn(
                        'transition-colors duration-150',
                        iconClassName,
                        isSelected ? '!text-white' : 'text-default-400'
                      )}
                      icon={item.icon}
                      width={24}
                      aria-label={`${item.title} иконка`}
                    />
                  </div>
                ) : (
                  <div className="icon size-6">
                    <div
                      className={cn(
                        'transition-colors duration-150',
                        isSelected && '[&_svg]:!text-white [&_.text-default-400]:!text-white'
                      )}
                    >
                      {item.startContent}
                    </div>
                  </div>
                ))}
              <span
                className={cn(
                  'flex-1',
                  isMobile && isNested ? 'text-inherit font-normal px-1' : null
                )}
              >
                {item.title}
              </span>
              <div
                className={cn(
                  'transition-colors duration-150',
                  isSelected && '[&_.sidebar-chip]:!text-white [&_.sidebar-chip]:!bg-white/20',
                  'font-medium'
                )}
              >
                {item.endContent}
              </div>
            </Link>
          </li>
        );
      },
      [iconClassName, isMobile, onItemClick, primaryColors.background, selectedKey]
    );

    const renderSection = React.useCallback(
      (item: SidebarItem) => {
        if (!item.items || item.items.length === 0) {
          return renderItem(item);
        }
        if (isMobile) {
          const isOpen = openKeys.has(item.key);
          const isGroupActive = item.items.some((subItem) => subItem.key === selectedKey);
          const sectionIcon = item.startContent ? (
            <div className="icon size-6 text-default-400">{item.startContent}</div>
          ) : item.icon ? (
            <div className="icon size-6 text-default-400">
              <Icon icon={item.icon} width={24} aria-label={`${item.title} иконка`} />
            </div>
          ) : null;
          return (
            <li key={item.key}>
              <button
                type="button"
                className={cn(
                  'flex w-full items-center justify-between rounded-large px-4 py-2.5 text-sm font-semibold',
                  'transition-colors duration-150',
                  isGroupActive ? 'text-auth-ink' : 'text-auth-muted',
                  isGroupActive ? 'bg-default-300/20' : 'hover:bg-default-300/20'
                )}
                onClick={() => toggleSection(item.key)}
                aria-expanded={isOpen}
                aria-controls={`sidebar-section-${item.key}`}
              >
                <span className="flex items-center gap-2">
                  {sectionIcon}
                  {item.title}
                </span>
                <Icon
                  icon="solar:alt-arrow-down-linear"
                  className={cn('text-default-400 transition-transform', isOpen && 'rotate-180')}
                  width={20}
                  aria-hidden="true"
                />
              </button>
              <AnimatePresence initial={false}>
                {isOpen && (
                  <m.ul
                    id={`sidebar-section-${item.key}`}
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    {(item.items ?? []).map((subItem, index, arr) =>
                      renderItem(subItem, true, index === arr.length - 1)
                    )}
                  </m.ul>
                )}
              </AnimatePresence>
            </li>
          );
        }
        return (
          <li key={item.key}>
            <div className="px-0 pt-8 mb-2">
              <Typography variant="label3" className="text-auth-muted">
                {item.title}
              </Typography>
            </div>
            <ul>{item.items.map((subItem) => renderItem(subItem))}</ul>
          </li>
        );
      },
      [isMobile, openKeys, renderItem, selectedKey, toggleSection]
    );

    return (
      <nav
        ref={ref}
        className={cn('w-full', className, {
          'opacity-50 pointer-events-none': currentIsPending,
        })}
        aria-label="Главное навигационное меню"
      >
        <ul>{items.map(renderSection)}</ul>
      </nav>
    );
  }
);

Sidebar.displayName = 'Sidebar';

const MemoizedSidebar = React.memo(Sidebar, (prevProps, nextProps) => {
  return (
    prevProps.selectedKey === nextProps.selectedKey &&
    prevProps.isPending === nextProps.isPending &&
    prevProps.items === nextProps.items
  );
});

export default MemoizedSidebar;
